'use client';

// # Task Detail Dialog
// Comprehensive task detail view with comments, timeline, and all task information

import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Building2,
  Calendar,
  Flag,
  Clock,
  MessageSquare,
  History,
  Tag as TagIcon,
  CheckCircle2,
  Circle,
  AlertCircle,
  Loader2,
  Send,
  Edit,
  Trash2,
  Plus,
  FileText,
  Users,
  Settings,
  Archive,
  ArchiveRestore,
  Bell,
  BellRing,
  Share2
} from 'lucide-react';
import { TaskService } from '../../lib/task-service';
import { ReminderService } from '../../lib/reminder-service';
import { useAuth } from '../../auth';
import { useTaskPermissions } from '../../hooks/useTaskPermissions';
import { useDashboardSafe } from '../../lib/dashboard-context';
import { CrucialSelector } from '../tasks/CrucialSelector';
import { ShareDialog } from '../share/ShareDialog';
import { useMemo } from 'react';
import type { TaskDetail, TaskComment, TaskEvent, TaskCommentFormData, TaskEventFormData, Priority, TaskStatus, TaskType, ContextType } from '../../types/tasks';

interface TaskDetailDialogProps {
  taskId: string;
  assignmentId?: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function TaskDetailDialog({
  taskId,
  assignmentId,
  isOpen,
  onClose,
  onUpdate
}: TaskDetailDialogProps) {
  const { user } = useAuth();
  
  // Task data
  const [taskDetail, setTaskDetail] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Comments
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [isInternalComment, setIsInternalComment] = useState(false);
  
  // Events/Timeline
  const [events, setEvents] = useState<TaskEvent[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState<TaskEventFormData>({
    event_type: 'custom',
    event_title: '',
    event_description: ''
  });
  const [addingEvent, setAddingEvent] = useState(false);
  
  // Assignments
  const [assignments, setAssignments] = useState<any[]>([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentSearchResults, setAssignmentSearchResults] = useState<any[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newAssignmentHandlerType, setNewAssignmentHandlerType] = useState<'primary' | 'sub' | 'assigned' | 'observer' | 'backup'>('assigned');
  const [newAssignmentComment, setNewAssignmentComment] = useState('');
  const [addingAssignment, setAddingAssignment] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState<string | null>(null);
  
  // Reminders
  const [reminders, setReminders] = useState<any[]>([]);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [newReminderTitle, setNewReminderTitle] = useState('');
  const [newReminderDescription, setNewReminderDescription] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('');
  const [newReminderPriority, setNewReminderPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [addingReminder, setAddingReminder] = useState(false);
  
  // Task editing
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editingTaskType, setEditingTaskType] = useState<TaskType>('business');
  const [editingContextType, setEditingContextType] = useState<ContextType>('department');
  const [editingIsCrucial, setEditingIsCrucial] = useState(false);
  const [editingCrucialForType, setEditingCrucialForType] = useState<'milestone' | 'sales' | 'budget' | 'presentation' | null>(null);
  const [editingCrucialForId, setEditingCrucialForId] = useState<string | null>(null);
  const [savingTask, setSavingTask] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'timeline' | 'settings'>('details');
  
  // Share dialog
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // Permissions
  const { permissions } = useTaskPermissions(taskId);
  // Use safe version to allow rendering outside DashboardProvider (e.g., from global notification badge)
  const { availableDepartments } = useDashboardSafe();
  
  // Check if Settings tab should be visible
  const showSettingsTab = useMemo(() => {
    if (!taskDetail || !user) return false;
    
    // Task creator
    if (taskDetail.created_by_user?.id === user.id) return true;
    
    // System admins/owners
    if (user.role === 'admin' || user.role === 'owner') return true;
    
    // Check permissions
    return permissions.canManageSettings;
  }, [taskDetail, user, permissions]);

  // Load task details
  useEffect(() => {
    if (isOpen && taskId) {
      loadTaskDetail();
    }
  }, [isOpen, taskId, assignmentId]);

  // Initialize editing state when task detail loads
  useEffect(() => {
    if (taskDetail) {
      setEditingTaskType(taskDetail.task_type || 'business');
      setEditingContextType(taskDetail.context_type || 'department');
      setEditingIsCrucial((taskDetail as any).is_crucial || false);
      setEditingCrucialForType((taskDetail as any).crucial_for_type || null);
      setEditingCrucialForId((taskDetail as any).crucial_for_id || null);
    }
  }, [taskDetail]);
  
  // Load assignments when switching to settings tab
  useEffect(() => {
    if (isOpen && taskId && activeTab === 'settings') {
      loadAssignments();
    }
  }, [activeTab, isOpen, taskId]);
  
  // Load reminders when task detail is loaded
  useEffect(() => {
    if (isOpen && taskId && taskDetail) {
      loadReminders();
    }
  }, [isOpen, taskId, taskDetail]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const loadTaskDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await TaskService.getTaskDetail(taskId, assignmentId);
      
      if (result.success && result.data) {
        setTaskDetail(result.data);
        setComments(result.data.comments || []);
        setEvents(result.data.events || []);
      } else {
        setError(result.error || 'Failed to load task details');
      }
      
      // Load assignments if on settings tab
      if (activeTab === 'settings' || showSettingsTab) {
        await loadAssignments();
      }
    } catch (err) {
      console.error('Error loading task detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to load task details');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAssignments = async () => {
    try {
      const result = await TaskService.getTaskAssignments(taskId);
      if (result.success && result.data) {
        setAssignments(result.data);
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
    }
  };
  
  // Search users for assignment
  useEffect(() => {
    if (assignmentSearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setAssignmentSearchResults([]);
    }
  }, [assignmentSearch]);
  
  const searchUsers = async () => {
    try {
      setSearchingUsers(true);
      const excludeIds = assignments.map(a => a.user_id);
      const result = await TaskService.searchUsersForAssignment(assignmentSearch, excludeIds);
      if (result.success && result.data) {
        setAssignmentSearchResults(result.data);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearchingUsers(false);
    }
  };
  
  const handleAddAssignment = async () => {
    if (!selectedUser || !taskId) return;
    
    try {
      setAddingAssignment(true);
      const result = await TaskService.assignTask(taskId, selectedUser.id, {
        handler_type: newAssignmentHandlerType,
        assignment_comment: newAssignmentComment || undefined,
        priority: 'medium',
        assignment_type: 'individual'
      });
      
      if (result.success) {
        await loadAssignments();
        // Reload events to show the new assignment event
        const eventsResult = await TaskService.getTaskEvents(taskId, assignmentId);
        if (eventsResult.success && eventsResult.data) {
          setEvents(eventsResult.data);
        }
        await loadTaskDetail(); // Reload to get updated events
        
        // Show success message with notification info
        const userName = selectedUser.first_name && selectedUser.last_name
          ? `${selectedUser.first_name} ${selectedUser.last_name}`
          : selectedUser.email || 'User';
        setAssignmentSuccess(`${userName} has been assigned and notified.`);
        
        // Clear form after a short delay
        setTimeout(() => {
          setShowAssignmentForm(false);
          setSelectedUser(null);
          setAssignmentSearch('');
          setNewAssignmentComment('');
          setNewAssignmentHandlerType('assigned');
          setAssignmentSuccess(null);
        }, 2000);
        
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to assign task');
      }
    } catch (err) {
      console.error('Error adding assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to add assignment');
    } finally {
      setAddingAssignment(false);
    }
  };
  
  const handleUpdateHandlerType = async (assignmentId: string, handlerType: 'primary' | 'sub' | 'assigned' | 'observer' | 'backup') => {
    try {
      // Get current assignment to find old handler type
      const assignment = assignments.find(a => a.id === assignmentId);
      const oldHandlerType = assignment?.handler_type || 'assigned';

      const result = await TaskService.updateAssignmentHandlerType(assignmentId, handlerType);
      if (result.success) {
        // Create timeline event for handler type change
        try {
          const eventsResult = await TaskService.createTaskEvent(taskId, {
            event_type: 'custom',
            event_title: `Assignment handler type changed`,
            event_description: `Handler type changed from ${oldHandlerType} to ${handlerType} for ${formatUserName(assignment?.user)}`,
            metadata: {
              assignment_id: assignmentId,
              old_handler_type: oldHandlerType,
              new_handler_type: handlerType
            }
          }, assignmentId);
          
          if (eventsResult.success) {
            setEvents(prev => [eventsResult.data!, ...prev]);
          }
        } catch (eventError) {
          console.warn('Failed to create handler type change event:', eventError);
        }

        await loadAssignments();
        // Reload events
        const eventsResult = await TaskService.getTaskEvents(taskId, assignmentId);
        if (eventsResult.success && eventsResult.data) {
          setEvents(eventsResult.data);
        }
        await loadTaskDetail();
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to update handler type');
      }
    } catch (err) {
      console.error('Error updating handler type:', err);
      setError(err instanceof Error ? err.message : 'Failed to update handler type');
    }
  };
  
  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;
    
    try {
      // Get assignment details before removal for event
      const assignment = assignments.find(a => a.id === assignmentId);
      const assignedUserName = assignment ? formatUserName(assignment.user) : 'User';

      const result = await TaskService.removeAssignment(assignmentId);
      if (result.success) {
        // Create timeline event for assignment removal
        try {
          const eventsResult = await TaskService.createTaskEvent(taskId, {
            event_type: 'custom',
            event_title: `Assignment removed`,
            event_description: `Assignment removed for ${assignedUserName}`,
            metadata: {
              removed_assignment_id: assignmentId,
              removed_user_id: assignment?.user_id
            }
          }, assignmentId);
          
          if (eventsResult.success) {
            setEvents(prev => [eventsResult.data!, ...prev]);
          }
        } catch (eventError) {
          console.warn('Failed to create removal event:', eventError);
        }

        await loadAssignments();
        // Reload events
        const eventsResult = await TaskService.getTaskEvents(taskId, assignmentId);
        if (eventsResult.success && eventsResult.data) {
          setEvents(eventsResult.data);
        }
        await loadTaskDetail();
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to remove assignment');
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove assignment');
    }
  };
  
  // Load reminders for this task
  const loadReminders = async () => {
    if (!taskId || !user?.id) return;
    
    try {
      const taskReminders = await ReminderService.getRemindersForEntity('task', taskId);
      setReminders(taskReminders);
    } catch (err) {
      console.error('Error loading reminders:', err);
    }
  };
  
  // Create reminder linked to task
  const handleCreateReminder = async () => {
    if (!taskId || !user?.id || !newReminderTitle || !newReminderDate) return;
    
    try {
      setAddingReminder(true);
      
      // Combine date and time
      const remindAt = newReminderTime 
        ? `${newReminderDate}T${newReminderTime}:00`
        : `${newReminderDate}T09:00:00`;
      
      const reminder = await ReminderService.createReminder({
        title: newReminderTitle,
        description: newReminderDescription || undefined,
        remind_at: remindAt,
        priority: newReminderPriority,
        category: 'task',
        related_entity_type: 'task',
        related_entity_id: taskId,
        user_id: user.id,
        department_id: taskDetail?.department?.id,
        send_notification: true,
        is_recurring: false,
        visibility: 'private',
      });
      
      if (reminder) {
        // Create timeline event for reminder creation
        try {
          const eventsResult = await TaskService.createTaskEvent(taskId, {
            event_type: 'custom',
            event_title: `Reminder created: ${newReminderTitle}`,
            event_description: `Reminder set for ${new Date(remindAt).toLocaleString()}${newReminderDescription ? ` - ${newReminderDescription}` : ''}`,
            metadata: {
              reminder_id: reminder.id,
              reminder_date: remindAt,
              priority: newReminderPriority
            }
          }, assignmentId);
          
          if (eventsResult.success) {
            setEvents(prev => [eventsResult.data!, ...prev]);
          }
        } catch (eventError) {
          console.warn('Failed to create reminder event:', eventError);
        }

        await loadReminders();
        setShowReminderForm(false);
        setNewReminderTitle('');
        setNewReminderDescription('');
        setNewReminderDate('');
        setNewReminderTime('');
        setNewReminderPriority('medium');
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error creating reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to create reminder');
    } finally {
      setAddingReminder(false);
    }
  };
  
  // Complete reminder
  const handleCompleteReminder = async (reminderId: string) => {
    try {
      const success = await ReminderService.completeReminder(reminderId);
      if (success) {
        await loadReminders();
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error completing reminder:', err);
    }
  };
  
  // Delete reminder
  const handleDeleteReminder = async (reminderId: string) => {
    if (!confirm('Are you sure you want to delete this reminder? This action cannot be undone.')) return;
    
    try {
      const success = await ReminderService.deleteReminder(reminderId);
      if (success) {
        // Optimistically remove from reminders list
        setReminders(prev => prev.filter(r => r.id !== reminderId));
        await loadReminders();
        onUpdate?.();
      } else {
        setError('Failed to delete reminder. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete reminder');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !taskId) return;
    
    try {
      setAddingComment(true);
      
      const commentData: TaskCommentFormData = {
        content: newComment.trim(),
        is_internal: isInternalComment
      };
      
      const result = await TaskService.addTaskComment(taskId, commentData, assignmentId);
      
      if (result.success && result.data) {
        setComments(prev => [result.data!, ...prev]);
        setNewComment('');
        setIsInternalComment(false);
        // Reload events to get the new comment event
        const eventsResult = await TaskService.getTaskEvents(taskId, assignmentId);
        if (eventsResult.success && eventsResult.data) {
          setEvents(eventsResult.data);
        }
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to add comment');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const result = await TaskService.deleteTaskComment(commentId);
      
      if (result.success) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.event_title.trim() || !taskId) return;
    
    try {
      setAddingEvent(true);
      
      const result = await TaskService.createTaskEvent(taskId, newEvent, assignmentId);
      
      if (result.success && result.data) {
        setEvents(prev => [result.data!, ...prev]);
        setNewEvent({
          event_type: 'custom',
          event_title: '',
          event_description: ''
        });
        setShowEventForm(false);
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to add event');
      }
    } catch (err) {
      console.error('Error adding event:', err);
      setError(err instanceof Error ? err.message : 'Failed to add event');
    } finally {
      setAddingEvent(false);
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!taskDetail?.assignment || !user?.id) return;
    type AssignmentStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
    const statusToUpdate: AssignmentStatus = ['pending', 'in_progress', 'completed', 'cancelled'].includes(newStatus as AssignmentStatus) ? (newStatus as AssignmentStatus) : 'pending';
    try {
      const result = await TaskService.updateAssignmentStatus(
        taskDetail.assignment.id,
        statusToUpdate,
        user.id
      );
      
      if (result.success) {
        await loadTaskDetail(); // Reload to get updated events
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority: Priority) => {
    if (!taskDetail?.assignment) return;
    
    try {
      const result = await TaskService.updateAssignmentPriority(
        taskDetail.assignment.id,
        newPriority
      );
      
      if (result.success) {
        await loadTaskDetail(); // Reload to get updated events
        onUpdate?.();
      } else {
        setError(result.error || 'Failed to update priority');
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      setError(err instanceof Error ? err.message : 'Failed to update priority');
    }
  };

  // Helper: Get priority color
  const getPriorityColor = (priority?: Priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Helper: Get status icon
  const getStatusIcon = (status?: TaskStatus) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-blue-600" />;
      case 'cancelled': return <AlertCircle className="w-5 h-5 text-gray-400" />;
      default: return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  // Helper: Format user name
  const formatUserName = (userData?: { first_name?: string; last_name?: string; email?: string }) => {
    if (!userData) return 'Unknown';
    if (userData.first_name && userData.last_name) {
      return `${userData.first_name} ${userData.last_name}`;
    }
    return userData.email || 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[10002] overflow-hidden">
      <div className="bg-white w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 min-w-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  <span className="text-gray-500">Loading task...</span>
                </div>
              ) : taskDetail ? (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                    {taskDetail.title}
                  </h2>
                  {taskDetail.description && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                      {taskDetail.description}
                    </p>
                  )}
                </>
              ) : (
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Task Details</h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            {/* Share Button */}
            {taskDetail && (
              <button
                onClick={() => setShowShareDialog(true)}
                className="p-2 hover:bg-white/50 rounded transition-colors"
                aria-label="Share task"
                title="Share task"
              >
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
            )}
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 rounded transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : !taskDetail ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Failed to load task details</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Tabs */}
            <div className="border-b border-gray-200 px-4 sm:px-6 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              <div className="flex gap-1 min-w-max">
                {[
                  { id: 'details', label: 'Details', icon: FileText },
                  { id: 'comments', label: 'Comments', icon: MessageSquare, count: comments.length },
                  { id: 'timeline', label: 'Timeline', icon: History, count: events.length },
                  ...(showSettingsTab ? [{ id: 'settings', label: 'Settings', icon: Settings }] : [])
                ].map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                        isActive
                          ? 'border-blue-600 text-blue-600 font-medium'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                          isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6">
              {activeTab === 'details' && (
                <div className="space-y-6 min-w-0">
                  {/* Priority & Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <div className="flex gap-2">
                        {(['low', 'medium', 'high', 'urgent'] as Priority[]).map(priority => (
                          <button
                            key={priority}
                            onClick={() => handlePriorityChange(priority)}
                            className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${
                              taskDetail.assignment?.priority === priority
                                ? getPriorityColor(priority)
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Flag className="w-3.5 h-3.5" />
                              <span className="capitalize">{priority}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <div className="flex gap-2">
                        {(['pending', 'in_progress', 'completed', 'cancelled'] as TaskStatus[]).map(status => (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(status)}
                            className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors flex items-center gap-1.5 ${
                              taskDetail.assignment?.status === status
                                ? 'bg-blue-50 border-blue-300 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {getStatusIcon(status)}
                            <span className="capitalize">{status.replace('_', ' ')}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Assigned To */}
                  {taskDetail.assignment && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assigned To
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-900">
                          {formatUserName(taskDetail.assignment.user as any)}
                        </span>
                        {taskDetail.assignment.user?.email && (
                          <span className="text-xs text-gray-500">
                            ({taskDetail.assignment.user.email})
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Department */}
                  {taskDetail.department && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-900">
                          {taskDetail.department.display_name || taskDetail.department.name}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Tags/Categories */}
                  {taskDetail.tags && taskDetail.tags.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {taskDetail.tags.map(tag => (
                          <span
                            key={tag.id}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1"
                          >
                            <TagIcon className="w-3 h-3" />
                            {formatUserName(tag.user as any)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {taskDetail.assignment?.due_date && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Due Date
                        </label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">
                            {new Date(taskDetail.assignment.due_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}
                    {taskDetail.estimated_duration_minutes && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estimated Duration
                        </label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">
                            {taskDetail.estimated_duration_minutes} minutes
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {taskDetail.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {taskDetail.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Instructions */}
                  {taskDetail.instructions && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instructions
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          {taskDetail.instructions}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Reminders Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <BellRing className="w-5 h-5 text-blue-600" />
                        Reminders
                      </h3>
                      <button
                        onClick={() => setShowReminderForm(!showReminderForm)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Reminder</span>
                      </button>
                    </div>

                    {/* Add Reminder Form */}
                    {showReminderForm && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Reminder Title *
                            </label>
                            <input
                              type="text"
                              value={newReminderTitle}
                              onChange={(e) => setNewReminderTitle(e.target.value)}
                              placeholder="e.g., Review task before deadline"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea
                              value={newReminderDescription}
                              onChange={(e) => setNewReminderDescription(e.target.value)}
                              placeholder="Optional description..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                              rows={2}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date *
                              </label>
                              <input
                                type="date"
                                value={newReminderDate}
                                onChange={(e) => setNewReminderDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Time
                              </label>
                              <input
                                type="time"
                                value={newReminderTime}
                                onChange={(e) => setNewReminderTime(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Priority
                            </label>
                            <select
                              value={newReminderPriority}
                              onChange={(e) => setNewReminderPriority(e.target.value as any)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleCreateReminder}
                              disabled={!newReminderTitle || !newReminderDate || addingReminder}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {addingReminder ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                              <span>Create Reminder</span>
                            </button>
                            <button
                              onClick={() => {
                                setShowReminderForm(false);
                                setNewReminderTitle('');
                                setNewReminderDescription('');
                                setNewReminderDate('');
                                setNewReminderTime('');
                                setNewReminderPriority('medium');
                              }}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reminders List */}
                    <div className="space-y-2">
                      {reminders.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No reminders for this task</p>
                        </div>
                      ) : (
                        reminders.map((reminder) => {
                          const isOverdue = !reminder.is_completed && new Date(reminder.remind_at) < new Date();
                          const remindDate = new Date(reminder.remind_at);
                          
                          return (
                            <div
                              key={reminder.id}
                              className={`p-3 rounded-lg border flex items-start justify-between ${
                                isOverdue 
                                  ? 'bg-red-50 border-red-200' 
                                  : reminder.is_completed
                                  ? 'bg-gray-50 border-gray-200'
                                  : 'bg-white border-gray-200 hover:border-blue-300'
                              } transition-colors`}
                            >
                              <div className="flex items-start gap-3 flex-1">
                                <button
                                  onClick={() => handleCompleteReminder(reminder.id)}
                                  className="flex-shrink-0 mt-0.5"
                                >
                                  {reminder.is_completed ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-gray-400 hover:text-green-600" />
                                  )}
                                </button>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={`font-medium ${
                                      reminder.is_completed ? 'text-gray-500 line-through' : 'text-gray-900'
                                    }`}>
                                      {reminder.title}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      reminder.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                      reminder.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                      reminder.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {reminder.priority}
                                    </span>
                                    {isOverdue && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                        Overdue
                                      </span>
                                    )}
                                  </div>
                                  
                                  {reminder.description && (
                                    <p className="text-sm text-gray-600 mb-2">
                                      {reminder.description}
                                    </p>
                                  )}
                                  
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      <span>{remindDate.toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      <span>{remindDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleDeleteReminder(reminder.id)}
                                className="flex-shrink-0 p-1 hover:bg-red-100 rounded transition-colors"
                                title="Delete reminder"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'comments' && (
                <div className="space-y-4 min-w-0">
                  {/* Add Comment Form */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      rows={3}
                    />
                    <div className="flex items-center justify-between mt-3">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          className="rounded"
                        />
                        <span>Internal comment (team only)</span>
                      </label>
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || addingComment}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {addingComment ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        <span>Add Comment</span>
                      </button>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-4">
                    {comments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No comments yet. Be the first to comment!</p>
                      </div>
                    ) : (
                      comments.map(comment => (
                        <div
                          key={comment.id}
                          className={`border rounded-lg p-4 ${
                            comment.is_internal
                              ? 'bg-yellow-50 border-yellow-200'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {formatUserName(comment.user)}
                              </span>
                              {comment.is_internal && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
                                  Internal
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                              {comment.user_id === user?.id && (
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="p-1 hover:bg-red-100 rounded transition-colors"
                                  title="Delete comment"
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {comment.content}
                          </p>
                          {comment.edited_at && (
                            <p className="text-xs text-gray-400 mt-2">
                              (edited {new Date(comment.edited_at).toLocaleString()})
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-4 min-w-0">
                  {/* Add Event Form */}
                  {showEventForm && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mb-4">
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={newEvent.event_title}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, event_title: e.target.value }))}
                          placeholder="Event title..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <textarea
                          value={newEvent.event_description || ''}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, event_description: e.target.value }))}
                          placeholder="Event description (optional)..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                          rows={2}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleAddEvent}
                            disabled={!newEvent.event_title.trim() || addingEvent}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {addingEvent ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                            <span>Add Event</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowEventForm(false);
                              setNewEvent({ event_type: 'custom', event_title: '', event_description: '' });
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!showEventForm && (
                    <button
                      onClick={() => setShowEventForm(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Timeline Event</span>
                    </button>
                  )}

                  {/* Timeline */}
                  <div className="space-y-3">
                    {events.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No events yet. Timeline events will appear here.</p>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                        
                        {events.map((event, index) => (
                          <div key={event.id} className="relative flex gap-4 pb-4">
                            {/* Timeline dot */}
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 border-2 border-blue-500 rounded-full flex items-center justify-center z-10">
                              <History className="w-4 h-4 text-blue-600" />
                            </div>
                            
                            {/* Event content */}
                            <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {event.event_title}
                                  </h4>
                                  {event.event_description && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      {event.event_description}
                                    </p>
                                  )}
                                  {event.old_value && event.new_value && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Changed from <span className="font-medium">{event.old_value}</span> to <span className="font-medium">{event.new_value}</span>
                                    </p>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                  {new Date(event.occurred_at).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <User className="w-3 h-3" />
                                <span>{formatUserName(event.performed_by_user)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6 min-w-0">
                  {/* Task Metadata */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Created By
                        </label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">
                            {formatUserName(taskDetail.created_by_user)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Created At
                        </label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">
                            {new Date(taskDetail.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {taskDetail.updated_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Modified
                          </label>
                          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-900">
                              {new Date(taskDetail.updated_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Task ID
                        </label>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <code className="text-xs text-gray-600 font-mono break-all">
                            {taskId}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Task Type and Context Type */}
                  {permissions.canManageSettings && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Task Configuration</h3>
                        {!isEditingTask && (
                          <button
                            onClick={() => setIsEditingTask(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                        )}
                      </div>

                      {isEditingTask ? (
                        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          {/* Task Type */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Task Type
                            </label>
                            <select
                              value={editingTaskType}
                              onChange={(e) => setEditingTaskType(e.target.value as TaskType)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="business">Business</option>
                              <option value="administrative">Administrative</option>
                              <option value="todo">Todo</option>
                              <option value="custom">Custom</option>
                              <option value="survey">Survey</option>
                              <option value="measurement">Measurement</option>
                              <option value="upload">Upload</option>
                            </select>
                          </div>

                          {/* Context Type */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Context Type
                            </label>
                            <select
                              value={editingContextType}
                              onChange={(e) => setEditingContextType(e.target.value as ContextType)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="department">Department</option>
                              <option value="business">Business</option>
                              <option value="administrative">Administrative</option>
                              <option value="general">General</option>
                              <option value="study">Study</option>
                              <option value="project">Project</option>
                              <option value="custom">Custom</option>
                              <option value="presentation">Presentation</option>
                              <option value="crm_lead">CRM Lead</option>
                            </select>
                          </div>

                          {/* Crucial Selector */}
                          <CrucialSelector
                            isCrucial={editingIsCrucial}
                            crucialForType={editingCrucialForType}
                            crucialForId={editingCrucialForId}
                            departmentId={taskDetail.department?.id}
                            onChange={(crucial, type, id) => {
                              setEditingIsCrucial(crucial);
                              setEditingCrucialForType(type);
                              setEditingCrucialForId(id);
                            }}
                            disabled={savingTask}
                          />

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-2">
                            <button
                              onClick={async () => {
                                try {
                                  setSavingTask(true);
                                  
                                  // Track changes for event
                                  const changes: string[] = [];
                                  if (taskDetail.task_type !== editingTaskType) {
                                    changes.push(`Task type: ${taskDetail.task_type} → ${editingTaskType}`);
                                  }
                                  if (taskDetail.context_type !== editingContextType) {
                                    changes.push(`Context type: ${taskDetail.context_type} → ${editingContextType}`);
                                  }
                                  const wasCrucial = (taskDetail as any).is_crucial || false;
                                  if (wasCrucial !== editingIsCrucial) {
                                    changes.push(`Crucial: ${wasCrucial ? 'Yes' : 'No'} → ${editingIsCrucial ? 'Yes' : 'No'}`);
                                  }
                                  if ((taskDetail as any).crucial_for_type !== editingCrucialForType) {
                                    changes.push(`Crucial for: ${(taskDetail as any).crucial_for_type || 'None'} → ${editingCrucialForType || 'None'}`);
                                  }

                                  const result = await TaskService.updateTask(taskId, {
                                    task_type: editingTaskType,
                                    context_type: editingContextType,
                                    is_crucial: editingIsCrucial,
                                    crucial_for_type: editingCrucialForType,
                                    crucial_for_id: editingCrucialForId
                                  });

                                  if (result.success) {
                                    // Create timeline event for configuration changes
                                    if (changes.length > 0) {
                                      try {
                                        const eventsResult = await TaskService.createTaskEvent(taskId, {
                                          event_type: 'custom',
                                          event_title: 'Task configuration updated',
                                          event_description: changes.join('; '),
                                          metadata: {
                                            task_type: editingTaskType,
                                            context_type: editingContextType,
                                            is_crucial: editingIsCrucial,
                                            crucial_for_type: editingCrucialForType,
                                            crucial_for_id: editingCrucialForId
                                          }
                                        }, assignmentId);
                                        
                                        if (eventsResult.success && eventsResult.data) {
                                          setEvents(prev => [eventsResult.data!, ...prev]);
                                        }
                                      } catch (eventError) {
                                        console.warn('Failed to create configuration change event:', eventError);
                                      }
                                    }

                                    setIsEditingTask(false);
                                    // Reload events
                                    const eventsResult = await TaskService.getTaskEvents(taskId, assignmentId);
                                    if (eventsResult.success && eventsResult.data) {
                                      setEvents(eventsResult.data);
                                    }
                                    await loadTaskDetail();
                                    onUpdate?.();
                                  } else {
                                    setError(result.error || 'Failed to update task');
                                  }
                                } catch (err) {
                                  console.error('Error updating task:', err);
                                  setError(err instanceof Error ? err.message : 'Failed to update task');
                                } finally {
                                  setSavingTask(false);
                                }
                              }}
                              disabled={savingTask}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {savingTask ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4" />
                              )}
                              <span>Save Changes</span>
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingTask(false);
                                // Reset to original values
                                if (taskDetail) {
                                  setEditingTaskType(taskDetail.task_type || 'business');
                                  setEditingContextType(taskDetail.context_type || 'department');
                                  setEditingIsCrucial((taskDetail as any).is_crucial || false);
                                  setEditingCrucialForType((taskDetail as any).crucial_for_type || null);
                                  setEditingCrucialForId((taskDetail as any).crucial_for_id || null);
                                }
                              }}
                              disabled={savingTask}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Task Type
                            </label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-900 capitalize">
                                {taskDetail.task_type || 'Not set'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Context Type
                            </label>
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-900 capitalize">
                                {taskDetail.context_type || 'Not set'}
                              </span>
                            </div>
                          </div>
                          {((taskDetail as any).is_crucial) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Crucial For
                              </label>
                              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-amber-900">
                                    {(taskDetail as any).crucial_for_type || 'Unknown'}
                                  </span>
                                  {(taskDetail as any).crucial_for_id && (
                                    <span className="text-xs text-amber-700">
                                      (ID: {(taskDetail as any).crucial_for_id.substring(0, 8)}...)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Department Assignment */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Department</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Department
                      </label>
                      {taskDetail.department ? (
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">
                            {taskDetail.department.display_name || taskDetail.department.name}
                          </span>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <span className="text-sm text-gray-500">No department assigned</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Permission Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
                    <div className="space-y-2">
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-900">
                          <strong>Who can edit this task:</strong>
                        </p>
                        <ul className="mt-2 space-y-1 text-sm text-blue-800 list-disc list-inside">
                          <li>Task creator ({formatUserName(taskDetail.created_by_user)})</li>
                          {taskDetail.department && (
                            <li>Department admins/executives in {taskDetail.department.display_name || taskDetail.department.name}</li>
                          )}
                          <li>Executives in Administration department</li>
                          <li>System administrators and owners</li>
                        </ul>
                      </div>
                      {taskDetail.assignment && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-900">
                            <strong>Assigned user:</strong> {formatUserName(taskDetail.assignment.user as any)} can edit their assignment (status, priority, comments)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Task Assignments */}
                  {permissions.canAssign && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Assignments</h3>
                        <button
                          onClick={() => setShowAssignmentForm(!showAssignmentForm)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Assign User</span>
                        </button>
                      </div>
                      
                      {/* Add Assignment Form */}
                      {showAssignmentForm && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          {/* Success Message */}
                          {assignmentSuccess && (
                            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-green-800">{assignmentSuccess}</p>
                                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                  <Bell className="w-3 h-3" />
                                  Notification sent to assigned user
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-3">
                            {/* User Search */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search User
                              </label>
                              <input
                                type="text"
                                value={assignmentSearch}
                                onChange={(e) => setAssignmentSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                              {searchingUsers && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Searching...</span>
                                </div>
                              )}
                              {assignmentSearchResults.length > 0 && (
                                <div className="mt-2 border border-gray-200 rounded-lg bg-white max-h-40 overflow-y-auto">
                                  {assignmentSearchResults.map(user => (
                                    <button
                                      key={user.id}
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setAssignmentSearch(`${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim());
                                        setAssignmentSearchResults([]);
                                      }}
                                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors text-sm ${
                                        selectedUser?.id === user.id ? 'bg-blue-50' : ''
                                      }`}
                                    >
                                      <div className="font-medium">
                                        {user.first_name && user.last_name
                                          ? `${user.first_name} ${user.last_name}`
                                          : user.email}
                                      </div>
                                      <div className="text-xs text-gray-500">{user.email}</div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Handler Type */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Handler Type
                              </label>
                              <select
                                value={newAssignmentHandlerType}
                                onChange={(e) => setNewAssignmentHandlerType(e.target.value as any)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="primary">Primary Handler (Main responsible)</option>
                                <option value="sub">Sub Handler (Secondary)</option>
                                <option value="assigned">Assigned (Regular assignment)</option>
                                <option value="observer">Observer (View only)</option>
                                <option value="backup">Backup (Standby)</option>
                              </select>
                            </div>
                            
                            {/* Assignment Comment */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assignment Comment
                              </label>
                              <textarea
                                value={newAssignmentComment}
                                onChange={(e) => setNewAssignmentComment(e.target.value)}
                                placeholder="Add a comment about this assignment..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                rows={2}
                              />
                            </div>
                            
                            {/* Info about notifications */}
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                              <Bell className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-blue-700">
                                <strong>Note:</strong> The assigned user will automatically receive a notification via in-app, email, and push channels when you assign this task.
                              </p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleAddAssignment}
                                disabled={!selectedUser || addingAssignment}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {addingAssignment ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Bell className="w-4 h-4" />
                                    <Plus className="w-4 h-4" />
                                  </>
                                )}
                                <span>Assign & Notify</span>
                              </button>
                              <button
                                onClick={() => {
                                  setShowAssignmentForm(false);
                                  setSelectedUser(null);
                                  setAssignmentSearch('');
                                  setNewAssignmentComment('');
                                  setNewAssignmentHandlerType('assigned');
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Assignments List */}
                      <div className="space-y-2">
                        {assignments.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                            <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No assignments yet</p>
                          </div>
                        ) : (
                          assignments.map(assignment => (
                            <div
                              key={assignment.id}
                              className="p-3 bg-white border border-gray-200 rounded-lg flex items-start justify-between"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <User className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium text-sm text-gray-900">
                                    {formatUserName(assignment.user)}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    assignment.handler_type === 'primary'
                                      ? 'bg-red-100 text-red-700'
                                      : assignment.handler_type === 'sub'
                                      ? 'bg-orange-100 text-orange-700'
                                      : assignment.handler_type === 'observer'
                                      ? 'bg-gray-100 text-gray-700'
                                      : assignment.handler_type === 'backup'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {assignment.handler_type === 'primary' ? 'Primary' :
                                     assignment.handler_type === 'sub' ? 'Sub' :
                                     assignment.handler_type === 'observer' ? 'Observer' :
                                     assignment.handler_type === 'backup' ? 'Backup' : 'Assigned'}
                                  </span>
                                </div>
                                {assignment.assignment_comment && (
                                  <p className="text-xs text-gray-600 mt-1 italic">
                                    "{assignment.assignment_comment}"
                                  </p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span>Assigned by: {formatUserName(assignment.assigned_by_user)}</span>
                                  <span>{new Date(assignment.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  value={assignment.handler_type || 'assigned'}
                                  onChange={(e) => handleUpdateHandlerType(assignment.id, e.target.value as any)}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="primary">Primary</option>
                                  <option value="sub">Sub</option>
                                  <option value="assigned">Assigned</option>
                                  <option value="observer">Observer</option>
                                  <option value="backup">Backup</option>
                                </select>
                                <button
                                  onClick={() => handleRemoveAssignment(assignment.id)}
                                  className="p-1 hover:bg-red-100 rounded transition-colors"
                                  title="Remove assignment"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Advanced Actions */}
                  {permissions.canDelete && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Danger Zone</h3>
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-red-900">Delete Task</p>
                            <p className="text-xs text-red-700 mt-1">
                              This action cannot be undone. All assignments, comments, and events will be permanently deleted.
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!confirm('Are you sure you want to delete this task? This action cannot be undone. All assignments, comments, and events will be permanently deleted.')) return;
                              
                              try {
                                setError(null);
                                const result = await TaskService.deleteTask(taskId);
                                
                                if (result.success) {
                                  onUpdate?.();
                                  onClose();
                                } else {
                                  setError(result.error || 'Failed to delete task. Please try again.');
                                }
                              } catch (err) {
                                console.error('Error deleting task:', err);
                                setError(err instanceof Error ? err.message : 'Failed to delete task');
                              }
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete Task</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Share Dialog */}
      {showShareDialog && taskDetail && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          itemType="task"
          itemId={taskId}
          itemTitle={taskDetail.title}
        />
      )}
    </div>
  );
}

